import apiService from "@/api-service";
import HeaderedContainer from "@/components/headered-container/headered-container";
import { Hoofdcode } from "@/models/hoofdcode";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, XAxis, YAxis, Bar, Tooltip } from "recharts";

interface ResponseInterface {
    id: number;
    count: number;
    bestekken_count: number;
    hoofdcode: Hoofdcode;
}

function createHoofdCodeLabel(h: Hoofdcode): string {
    return `${h.werk_categorie_code}.${h.subwerk_categorie_code}.${h.volgnummer} (${h.versie_jaarversie})`;
}

export default function FrequentHoofdcodesChart(props: { reverse?: boolean, className?: string, card?: boolean }) {
    const navigate = useNavigate();
    const [data, setData] = useState<ResponseInterface[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await apiService.post<ResponseInterface[]>(
                'stats/frequent-hoofdcodes',
                { limit: 10, ...(props.reverse ? { reverse: true } : {}) }
            );
            const result = response.data ?? [];
            setData(props.reverse ? result.reverse() : result);
        } catch (err) {
            console.error(err);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    }, [props.reverse]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const title = props.reverse ? 'Minst voorkomende hoofdcodes' : 'Meest voorkomende hoofdcodes';

    if (isLoading) {
        return (
            <HeaderedContainer title={title} className={props.className} card={props.card}>
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13 }}>
                    Laden...
                </div>
            </HeaderedContainer>
        );
    }

    if (!data || data.length === 0) {
        return (
            <HeaderedContainer title={title} className={props.className} card={props.card}>
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13 }}>
                    Geen data beschikbaar
                </div>
            </HeaderedContainer>
        );
    }

    // Least frequent: render as 2-column ranked list (bar chart with all-zeros is meaningless)
    if (props.reverse) {
        const half = Math.ceil(data.length / 2);
        const columns = [data.slice(0, half), data.slice(half)];

        return (
            <HeaderedContainer title={title} className={props.className} card={props.card}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                    {columns.map((col, colIdx) => (
                        <div key={colIdx}>
                            {col.map((item, i) => {
                                const globalIdx = colIdx * half + i;
                                const label = item.hoofdcode ? createHoofdCodeLabel(item.hoofdcode) : `ID: ${item.id}`;
                                const description = item.hoofdcode?.romptekst || item.hoofdcode?.hoofdtekst || '';
                                const isLastInCol = i === col.length - 1;
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => navigate(`/hoofdcodes/${item.id}`)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '9px 0',
                                            borderBottom: isLastInCol ? 'none' : '1px solid #f5f5f5',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <span style={{ minWidth: 16, textAlign: 'right', fontSize: 11, color: '#ccc', fontWeight: 600, flexShrink: 0 }}>
                                            {globalIdx + 1}
                                        </span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>{label}</div>
                                            {description && (
                                                <div style={{ fontSize: 11, color: '#999', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {description}
                                                </div>
                                            )}
                                        </div>
                                        <span style={{ fontSize: 11, color: '#ccc', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            {item.count ?? 0}×
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </HeaderedContainer>
        );
    }

    // Most frequent: bar chart
    const tooltipLabelFormatter = (label: string) => {
        const item = data.find(h => h.hoofdcode && createHoofdCodeLabel(h.hoofdcode) === label);
        if (!item?.hoofdcode) return label;
        const desc = item.hoofdcode.romptekst || item.hoofdcode.hoofdtekst || '';
        return desc ? `${label} – ${desc}` : label;
    };

    const handleBarClick = (item: ResponseInterface) => navigate(`/hoofdcodes/${item.id}`);
    const handleYAxisClick = (d: any) => {
        const item = data.find(h => h.hoofdcode && createHoofdCodeLabel(h.hoofdcode) === d.value);
        if (item) navigate(`/hoofdcodes/${item.id}`);
    };
    const createYAxisLabel = (item: ResponseInterface) =>
        item.hoofdcode ? createHoofdCodeLabel(item.hoofdcode) : `ID: ${item.id}`;

    return (
        <HeaderedContainer title={title} className={props.className} card={props.card}>
            <ResponsiveContainer width="100%" height={300} minWidth={250}>
                <BarChart layout="vertical" data={data}>
                    <YAxis onClick={handleYAxisClick} width={75} type="category" dataKey={createYAxisLabel} tick={{ fontSize: 11, fill: '#555' }} />
                    <XAxis type="number" hide />
                    <Tooltip
                        labelFormatter={tooltipLabelFormatter}
                        formatter={(value: any) => [(value as number).toString(), 'Vermeldingen']}
                    />
                    <Bar
                        onClick={handleBarClick}
                        dataKey="count"
                        fill="#009de0"
                        radius={[0, 3, 3, 0]}
                        label={{ position: 'right', fontSize: 11, fill: '#666' }}
                    />
                </BarChart>
            </ResponsiveContainer>
        </HeaderedContainer>
    );
}
